<template>
    <div>
        <Heading class="tw-mt-10" :title="pageTitle" :size="1" :class="{ 'tw-text-center': isAsiInquiry }"/>
        <info-snippet 
            hasBackground
            v-if="isFollowUpInquiry"
        >
            <p>
                Unsere Erfahrung zeigt, dass ein paar Details unseren lokalen Experten bei der Reiseplanung enorm helfen und wirklich einen Unterschied machen.
            </p>
            <p class="tw-font-medium">
                Bitte ergänze Deine Reisewünsche. Wir können Deine Anfrage sonst leider nicht an einen unserer lokalen Experten weiterleiten.
            </p>
        </info-snippet>
        <div
            v-if="isAsiInquiry"
            class="tw-my-6 tw-bg-white tw-rounded-lg tw-border tw-border-gray-300 tw-p-6 md:tw-p-10 tw-text-center"
        >
            <div class="tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-justify-center lg:tw-gap-3 tw-mb-4">
                <img
                    src="/bundles/website/assets/asi-logo-card.svg"
                    alt="ASI Reisen"
                    class="tw-h-16 tw-w-auto"
                />
                <span aria-hidden="true" class="tw-text-2xl tw-text-gray-500">&rarr;</span>
                <img
                    src="/bundles/website/assets/wdt-logo-card.svg"
                    alt="WeDesignTrips"
                    class="tw-h-16 tw-w-auto"
                />
            </div>
            <p>
                WeDesignTrips ist ein Tochterunternehmen von ASI Reisen — gleiches Haus, gleiches Team, gleiches ASI-Qualitätsversprechen.
            </p>
        </div>
        <Heading
            title="So geht's jetzt weiter:"
            :size="2"
            :class="{ '!tw-text-3xl !tw-mt-14 !tw-mb-6': isAsiInquiry }"
        />
        <div v-if="isPhoneInquiry">
            <next-steps-snippet :number="1" >
                Wir melden uns zeitnah, um Deine Reisewünsche kurz vorzubesprechen.
                <custom-button @click="loadCalendly()">
                    Wunschtermin wählen
                </custom-button>
            </next-steps-snippet>
            <next-steps-snippet :number="2" >
                Wir leiten Deine Anfrage an den passenden Experten im Reiseland weiter, der mit Dir zusammen Deine Reiseplanung abstimmt.
            </next-steps-snippet>
            <next-steps-snippet :number="3" >
                Du buchst und zahlst Deine Reise sicher über WeDesignTrips.
            </next-steps-snippet>
        </div>
        <div v-else-if="isFollowUpInquiry">
            <next-steps-snippet :number="1" >
                Ergänze hier Deine Reisewünsche.
                <custom-button @click="loadUpdateMessage()">
                    Jetzt Reisewünsche ergänzen &rarr;
                </custom-button>
            </next-steps-snippet>
            <next-steps-snippet :number="2" >
                Dein lokaler Experte plant die Reise nach Deinen Vorstellungen und Wünschen.
            </next-steps-snippet>
            <next-steps-snippet :number="3" >
                Freu Dich auf Deine Traumreise in {{ country?.title }}!
            </next-steps-snippet>
        </div>
        <div v-else-if="isAsiInquiry">
            <next-steps-snippet :number="1" >
                Dein persönlicher Reiseexperte von WeDesignTrips meldet sich zeitnah bei dir mit einem ersten Reisevorschlag.
            </next-steps-snippet>
            <next-steps-snippet :number="2" >
                Gemeinsam verfeinert ihr deine Reiseplanung - telefonisch oder per Chat.
            </next-steps-snippet>
            <next-steps-snippet :number="3" >
                Du buchst und zahlst deine Reise sicher über WeDesignTrips – mit ASI Qualitätsgarantie.
            </next-steps-snippet>
        </div>
        <div v-else>
            <next-steps-snippet :number="1" >
                Dein lokaler Experte meldet sich zeitnah bei Dir mit einem ersten Reisevorschlag.
            </next-steps-snippet>
            <next-steps-snippet :number="2" >
                Gemeinsam verfeinert ihr Deine Reiseplanung - telefonisch oder per Chat.
            </next-steps-snippet>
            <next-steps-snippet :number="3" >
                Du buchst und zahlst Deine Reise sicher über WeDesignTrips
            </next-steps-snippet>
        </div>
    </div>
</template>

<script>
import { LONG_MESSAGE_THRESHOLD } from '../components/mixin/acquisitionConstants.js';
import CustomButton from "../components/baseComponents/CustomButton.vue";
import NextStepsSnippet from "../components/successScreenComponents/NextStepsSnippet.vue";
import InfoSnippet from "../components/travelDataComponents/InfoSnippet.vue";
import { mapGetters } from 'vuex';

export default {
    name: 'SuccessPage',
    components: {NextStepsSnippet, CustomButton, InfoSnippet},
    computed: {
        ...mapGetters({
            country: 'tour/country',
            tour: 'tour/route',
            travelData: 'tour/travelData',
            inquiry: 'tour/inquiry',
            isAsiInquiry: 'tour/isAsiInquiry',
        }),
        isPhoneInquiry() {
            return this.travelData?.inquiryType === 'phone-inquiry';
        },
        isFollowUpInquiry() {
            const message = this.travelData?.message || '';
            // Get hasExistingBookings from router params, store is being updated too slow
            const hasExistingBookings = this.$route.params.hasExistingBookings !== undefined 
                ? this.$route.params.hasExistingBookings
                : false;
            return (this.country?.mainCountry.inquiryType === 'follow-up-inquiry') && 
                message.trim().length < this.longMessageThreshold && 
                this.tour === null &&
                !hasExistingBookings;
        },
        pageTitle() {
            const firstName = this.travelData?.user?.firstName;
            if (this.isAsiInquiry) {
                return `Danke für deine Anfrage, ${firstName} — wir leiten sie an WeDesignTrips weiter`;
            }
            return `Danke für Deine Anfrage, ${firstName}!`;
        },
        longMessageThreshold() {
            return LONG_MESSAGE_THRESHOLD;
        }
    },
    methods: {
        loadCalendly() {
            document.location.href = 'https://calendly.com/vincent-rabe-wedesigntrips/reisewuensche';
        },
        loadUpdateMessage() {
            // Get the inquiry ID from the store
            const inquiryId = this.inquiry?.inquiry;
            
            if (!inquiryId) {
                console.error('No inquiry ID found in store');
                return;
            }
            
            // Get the user email from the store
            const userEmail = this.travelData?.user?.email;
            
            if (!userEmail) {
                console.error('No user email found in store');
                return;
            }
            
            // Generate the update-message URL using the deep link service
            // This will create a secure URL that authenticates the user and redirects to the update-message page
            fetch(`/api/generate-update-message-url`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: userEmail,
                    inquiryId: inquiryId
                })
            })
            .then(response => response.json())
            .then(data => {
                // Handle the response format: [{"url": "..."}]
                const responseData = Array.isArray(data) ? data[0] : data;
                
                if (responseData && responseData.url) {
                    // Extract the path from the full URL for router navigation
                    const url = new URL(responseData.url);
                    const path = url.pathname + url.search;
                    
                    // Navigate using window.location.href as the generated URL is a deep link and needs to be reloaded
                    window.location.href = path;
                } else {
                    console.error('Failed to generate update message URL:', responseData);
                    alert('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
                }
            })
            .catch(error => {
                console.error('Error generating update message URL:', error);
                alert('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
            });
        },
    },
    mounted() {
        this.$store.commit('tour/setPending', false);
    }
}

</script>

<style scoped>

</style>